import { z } from 'zod';
/**
 * Donate Preview REST DTO — Phase 7 / Plan 07-04 (HUB-03).
 *
 * 엔드포인트 (공개 — SessionMiddleware `.exclude('api/v1/public/*splat')`):
 *   - `GET /api/v1/public/streamers/:slug/donate-preview` — `/@{streamer}` 결제 페이지 shell.
 *
 * Public-streamer 엔드포인트 (Plan 07-03) 와 비교:
 *   - 공개 정보 동일 (id/slug/displayName/avatar) + **결제 전용 메타** (amountPresets, fee disclosure,
 *     통신판매업 신고번호 placeholder). 결제 페이지 당 1회만 호출되므로 분리 — `/s/{slug}` 의 ISR
 *     캐시 tag 와 독립 관리 (`donate-preview:${slug}`).
 *   - 로그인 없이 접근 가능 (비로그인 사용자는 preview 본 후 `/login?redirect=` 로 진입).
 *
 * 핵심 불변 (ENG-REVIEW A3 / STACK.md Pitfall 2 — public-streamer 와 동일):
 *   - **LIVE 상태 필드 (isLive · viewerCount) 는 절대 포함되지 않는다** — 결제 페이지 is not LIVE
 *     dependent. 진입 시점의 방송 상태는 결제 흐름에 무관하므로 스키마 자체에서 필드 부재.
 *   - Zod `.strict()` 로 실수로 추가된 필드를 런타임 reject.
 *
 * 위협 모델 (07-04-PLAN §threat_model T-07-04-03):
 *   - email / phone / tossSellerId / kyc_* 등 PII 금지. controller SELECT 에서도 한정.
 *
 * Wave 0 / Wave 1 범위:
 *   - `reportingBusinessNumber` — Wave 0 항상 null (Plan 07-05 에서 실값 주입). 팬 결제 페이지
 *     footer 에 "통신판매업 신고번호: 준비 중 (Plan 07-05)" placeholder 로 노출되어 regulatory
 *     미준수가 가시화된다.
 *   - `amountPresets` — Wave 0 하드코디드 [1000, 5000, 10000, 50000]. Wave 1 에서 스트리머별
 *     preset 관리 UI 도입 시 DB column (streamer_donate_settings.amount_presets) 로 교체.
 *
 * 경계 규칙:
 *   - `minAmountKrw: 1000` / `maxAmountKrw: 1_000_000` — DESIGN.md §5.3 1,000 / 1,000,000 KRW.
 *   - `amountPresets` 은 반드시 `minAmountKrw <= x <= maxAmountKrw` 범위 (Zod refine).
 *   - `amountPresets` length 3-6 (UI 칩 배치 고려 — 칩 + "사용자 입력" 합산 4-7 개).
 */
/**
 * 공개 결제 preview 응답.
 *
 * 결제 페이지 한 번에 필요한 데이터 전부 — 금액/수수료/신고번호 placeholder 포함.
 * isLive/viewerCount 는 필드 자체 부재 (ENG-REVIEW A3).
 */
export const donatePreviewResponseSchema = z
    .object({
    streamerId: z.string().uuid(),
    channelSlug: z.string().min(1).max(64),
    displayName: z.string(),
    avatarUrl: z.string().url().nullable(),
    /**
     * `streamers.toss_seller_status in ('PARTIALLY_APPROVED', 'FULLY_APPROVED')` 일 때만 true.
     * false 면 결제 페이지가 "후원을 받지 않습니다" placeholder 로 카드 전체 교체.
     */
    donateEnabled: z.boolean(),
    /**
     * 빠른 금액 선택 칩. Wave 0 하드코디드 [1000, 5000, 10000, 50000] (4 개).
     * 3~6 개 허용 (UI 칩 + "사용자 입력" 칩 합산 4-7 개).
     */
    amountPresets: z.array(z.number().int().positive()).min(3).max(6),
    /** 최소 후원액 (원). DESIGN.md §5.3 = 1,000. */
    minAmountKrw: z.number().int().positive(),
    /** 최대 후원액 (원). DESIGN.md §5.3 = 1,000,000. */
    maxAmountKrw: z.number().int().positive(),
    /**
     * 통신판매업 신고번호 placeholder. Wave 0 = null (Plan 07-05 에서 실값 주입 예정).
     * DonateLegalFooter 가 null 일 때 "준비 중 (Plan 07-05)" 문구로 표시.
     */
    reportingBusinessNumber: z.string().nullable(),
    /**
     * 수수료 공시 문구. Wave 0 고정값 (api/payment/fees.ts Case A 정합):
     *   "팬 10% + 스트리머 10% (영수증에 명시)"
     *
     * Wave 1 에 스트리머별 수수료 티어 도입 시 controller 가 env 기반으로 동적 생성.
     */
    feeDisclosure: z.string(),
    /** 서버 snapshot 시각 — preview 갱신 주기 관측 참고. */
    fetchedAt: z.string().datetime({ offset: true }),
})
    .strict()
    .refine((v) => v.minAmountKrw < v.maxAmountKrw, { message: 'minAmountKrw must be less than maxAmountKrw' })
    .refine((v) => v.amountPresets.every((p) => p >= v.minAmountKrw && p <= v.maxAmountKrw), { message: 'all amountPresets must be within [minAmountKrw, maxAmountKrw]' });
//# sourceMappingURL=donate-preview.rest.js.map